import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FiMail } from "react-icons/fi";
import { useToast } from "../../components/Toast/ToastContext";
import { hasPendingPlan } from "../../services/webBilling";
import { EMAIL_NOT_VERIFIED, login } from "../../store/auth/actions";
import { resendVerificationApi } from "../../services/authApi";
import { AppDispatch } from "../../store/store";
import {
  AuthButton,
  AuthError,
  AuthField,
  AuthHeader,
  AuthLink,
  AuthSwitch,
  PasswordField,
} from "./authUi";

interface LoginFormProps {
  onClose?: () => void;
  onNeedsVerification?: (email: string, password: string) => void;
  onForgotPassword: () => void;
  onSwitchToSignup: () => void;
}

const Login: React.FC<LoginFormProps> = ({
  onClose,
  onNeedsVerification,
  onForgotPassword,
  onSwitchToSignup,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { notify } = useToast();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors: typeof fieldErrors = {};
    if (!email.trim()) errors.email = "Enter your email.";
    if (!password) errors.password = "Enter your password.";
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setError("");
    setLoading(true);
    try {
      await dispatch(login(email.trim(), password) as any);
      if (!localStorage.getItem("access_token")) {
        setError("Login failed. Please try again.");
        return;
      }
      notify("Welcome back!", { type: "success" });
      // Picked a paid plan before signing up: go back to finish checkout.
      navigate(hasPendingPlan() ? "/pricing" : "/inventory");
      onClose?.();
    } catch (err: any) {
      if (err?.code === EMAIL_NOT_VERIFIED) {
        // Old codes expire after an hour, so send a fresh one.
        await resendVerificationApi(err.email || email).catch(() => {});
        onNeedsVerification?.(err.email || email, password);
        return;
      }
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Login failed. Check your email and password."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AuthHeader title="Welcome back" subtitle="Log in to your VendorOrbis account." />
      <form onSubmit={handleSubmit} noValidate>
        <AuthError message={error} />
        <AuthField
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          autoFocus
          placeholder="you@example.com"
          icon={<FiMail />}
          value={email}
          error={fieldErrors.email}
          onChange={(e) => {
            setEmail(e.target.value);
            setFieldErrors((f) => ({ ...f, email: undefined }));
            setError("");
          }}
        />
        <PasswordField
          label="Password"
          name="password"
          autoComplete="current-password"
          value={password}
          error={fieldErrors.password}
          labelAction={
            <button
              type="button"
              onClick={onForgotPassword}
              className="text-xs font-medium text-slate-400 transition hover:text-white"
            >
              Forgot password?
            </button>
          }
          onChange={(e) => {
            setPassword(e.target.value);
            setFieldErrors((f) => ({ ...f, password: undefined }));
            setError("");
          }}
        />
        <AuthButton loading={loading}>Log in</AuthButton>
      </form>
      <AuthSwitch>
        New to VendorOrbis? <AuthLink onClick={onSwitchToSignup}>Create a free account</AuthLink>
      </AuthSwitch>
    </>
  );
};

export default Login;
